// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MultiSelectModalComponentProps } from '@interfaces/interfaces';
import { debounceTime, takeWhile } from 'rxjs';

@Component({
    selector: 'multi-select-modal',
    templateUrl: './multi-select-modal.component.html',
    styleUrls: ['./multi-select-modal.component.scss'],
})
export class MultiSelectModalComponent implements OnInit, OnDestroy {
    private isComponentAlive = true;

    public isLoading = true;

    // Display Configuration
    public title = 'Add Items';
    public description = 'Select the items you would like to add.';
    public btnText = 'Add Items';
    public search = 'Search for an item using any term in the item name';

    // Keys
    public searchKey: string;
    public compareKey: string;
    public returnedKey: string;

    // Item Management
    public availableItems: any[] = [];
    public filteredAvailableItems: any[] = [];
    public existingItems: any[] = [];
    public selectedItems: any[] = [];
    public itemsForm: FormGroup;
    public selectAll = false;

    constructor(
        private readonly dialogRef: MatDialogRef<MultiSelectModalComponent>,
        @Inject(MAT_DIALOG_DATA) public data: MultiSelectModalComponentProps
    ) {
        this.itemsForm = new FormGroup({
            search: new FormControl(''),
            selectAll: new FormControl(false),
        });
    }

    ngOnInit(): void {
        this.setItems();

        this.applyDialogContent();

        this.setKeys();

        this.filterAvailableItems();

        this.addFormEventListeners();

        this.isLoading = false;
    }

    setItems() {
        this.availableItems = this.data.availableItems;

        if (this.data?.existingItems) {
            this.existingItems = this.data.existingItems;
        }
    }

    applyDialogContent() {
        if (this.data?.title) {
            this.title = this.data?.title;
        }

        if (this.data?.description) {
            this.description = this.data?.description;
        }

        if (this.data?.btnText) {
            this.btnText = this.data?.btnText;
        }

        if (this.data?.search) {
            this.search = this.data?.search;
        }

        this.selectAll = this.data?.selectAll || false;
    }

    setKeys() {
        this.searchKey = this.data?.searchKey;
        this.compareKey = this.data?.compareKey;
        this.returnedKey = this.data?.returnedKey;

        if (!this.compareKey) {
            this.compareKey = this.returnedKey;
        }
    }

    addFormEventListeners() {
        this.itemsForm
            .get('search')
            .valueChanges.pipe(
                takeWhile(() => this.isComponentAlive),
                debounceTime(300) // wait for 300ms pause in events
            )
            .subscribe(async () => {
                await this.filterAvailableItems();
            });

        if (this.selectAll) {
            this.itemsForm
                .get('selectAll')
                .valueChanges.pipe(takeWhile(() => this.isComponentAlive))
                .subscribe(async (selectAll) => {
                    this.handleSelectAll(selectAll);
                });
        }
    }

    filterAvailableItems = () => {
        const searchValue = this.itemsForm.get('search').value.toLowerCase().trim();
        this.filteredAvailableItems = searchValue
            ? this.availableItems.filter((item) => {
                  return item[this.searchKey].toLowerCase().includes(searchValue);
              })
            : this.availableItems;
    };

    onItemSelectionChange(event) {
        this.selectedItems = event.source.selectedOptions.selected.map((option) => option.value);
    }

    existingItem = (form: any) => {
        return this.existingItems.some((existingForm) => (this.compareKey ? existingForm[this.compareKey] === form[this.returnedKey] : false));
    };

    itemSelected = (checkbox: any) => {
        return this.existingItem(checkbox) || this.selectedItems.some((s) => (this.compareKey ? s[this.compareKey] === checkbox[this.returnedKey] : false));
    };

    handleSelectAll = (selectAll = false) => {
        if (selectAll) {
            this.selectedItems = this.availableItems;
        } else {
            this.selectedItems = this.existingItems;
        }

        this.filterAvailableItems();
    };

    ngOnDestroy(): void {
        this.isComponentAlive = false;
    }

    close() {
        this.dialogRef.close();
    }

    async addItems() {
        const selectedItems = this.selectedItems.filter((x) => !this.existingItem(x)).map((x) => (this.returnedKey ? x[this.returnedKey] : x));
        this.dialogRef.close({ selectedItems });
    }
}
